import { z } from 'zod';

import { PREVIEW_FEATURES } from '@/types/preview-features';

const schema = z.object({
  VITE_API_URL: z.url(),
  // Optional on purpose: an environment must be able to boot before its analytics project
  // exists (the client UAT env did). Unset ⇒ `initPostHog()` returns without starting
  // PostHog at all, the same shape `VITE_SENTRY_DSN` already has in `main.tsx`.
  VITE_POSTHOG_KEY: z.string().min(1).optional(),
  VITE_POSTHOG_HOST: z.url().optional(),
  VITE_SENTRY_DSN: z.url().optional(),
  VITE_SENTRY_AUTH_TOKEN: z.string().optional(),
  VITE_GOOGLE_MAPS_API_KEY: z.string().optional(),
  // Cloud Map ID required for AdvancedMarkers on the Mapped Devices map. Optional — the map
  // component falls back to Google's demo id so pins render in dev/QA without cloud config.
  VITE_GOOGLE_MAPS_MAP_ID: z.string().optional(),
  VITE_RELEASE: z.string().optional(),
  // Deployment environment name ('qa' | 'uat' | 'production'), set per env block in
  // `revolte.yml`. Tags Sentry issues so two environments sharing one Sentry project stay
  // separable; `import.meta.env.MODE` cannot do this — every deployed build is 'production'.
  VITE_ENVIRONMENT: z.string().optional(),
  // Socket.io endpoint for server-originated notifications (ADR-0025). Optional on purpose:
  // unset ⇒ no connection is opened at all, so the client is dark until the backend deploys
  // and confirms the contract (FLAG-BE-6).
  VITE_REALTIME_URL: z.url().optional(),
  // FieldPro Tracking app, linked from the Dealer's My Devices ⓘ (feature 122138, G3). Differs per
  // environment; unset ⇒ the phrase renders as plain text, so no environment fails to boot.
  VITE_FIELDPRO_TRACKING_URL: z.url().optional(),
  // 'true' mocks /auth/*; 'false' forces auth to hit the real backend even when
  // VITE_MOCK_API is also 'true' (the QA setup — feature APIs mocked, auth real).
  // Unset defers to VITE_MOCK_API. Works in dev and in prod builds.
  VITE_MOCK_AUTH: z.string().optional(),
  // Set to 'true' to mock the whole backend (auth + feature APIs) with MSW.
  // Works in dev and in prod builds (QA, before the backend is deployed).
  VITE_MOCK_API: z.literal('true').optional(),
  // Comma list of top-level module ids (e.g. 'orders,discounts,payments') that hit the real
  // backend even while MSW is active — their handlers are not registered, so requests bypass to
  // VITE_API_URL. Every other module stays mocked. Empty/unset = all modules mocked.
  VITE_REAL_MODULES: z.string().optional(),
  // Comma list of capabilities to show although they are outside the release scope (ADR-0034),
  // e.g. 'customer-map,support'. Unset ⇒ none: every deployed environment leaves it unset, so a
  // forgotten variable can only hide a surface, never expose one. An unknown key fails the boot.
  VITE_PREVIEW_FEATURES: z
    .string()
    .optional()
    .transform((raw) =>
      (raw ?? '')
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean),
    )
    .pipe(z.array(z.enum(PREVIEW_FEATURES))),
});

export type Env = z.infer<typeof schema>;

export function parseEnv(raw: Record<string, string | undefined>): Env {
  // Treat empty strings as undefined so optional fields accept blank .env values.
  const coerced = Object.fromEntries(
    Object.entries(raw).map(([k, v]) => [k, v === '' ? undefined : v]),
  );
  const result = schema.safeParse(coerced);
  if (!result.success) {
    const issues = result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('\n');
    throw new Error(`Invalid environment configuration:\n${issues}`);
  }
  return result.data;
}

export const env: Env = parseEnv(import.meta.env as Record<string, string | undefined>);
